/**
 * Hideable Admin Home card — projects carrying at least one ACTIVE risk.
 *
 * The number is `projectsAtRisk` from GET /admin/risk/summary; the list behind
 * it is GET /admin/risk/projects (same predicate, same organization scope),
 * fetched only when the Admin asks for it.
 *
 *   - A pending read renders nothing: it is neither data nor "Unavailable".
 *   - A failed read, or a summary that says it could not count, is
 *     "Unavailable" — never 0.
 *   - 0 is a plain line, shown only when the read succeeded.
 *   - A project whose row can no longer be read is listed without a link,
 *     because the count above includes it.
 */
import { useState } from "react";
import { Link } from "react-router-dom";

import { Card, CardBody, CardHeader } from "@/components/ui/card/Card";
import { typography } from "@/design";
import {
  administrationApi,
  type ProjectsAtRiskList,
} from "@/features/administration/api/administration.api";
import { cn } from "@/lib/utils";

export type AdminHomeProjectsAtRiskProps = {
  /** null = not read yet; the object is the summary's own "could not count". */
  count: number | { status: "unavailable" } | null;
  failed: boolean;
  loading?: boolean;
};

export function AdminHomeProjectsAtRisk({
  count,
  failed,
  loading = false,
}: AdminHomeProjectsAtRiskProps) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState<ProjectsAtRiskList | null>(null);
  const [listFailed, setListFailed] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  if (loading && !failed && count === null) return null;

  const unavailable = failed || count === null || typeof count !== "number";

  const toggle = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    setListLoading(true);
    try {
      setList(await administrationApi.listProjectsAtRisk());
      setListFailed(false);
    } catch {
      setList(null);
      setListFailed(true);
    } finally {
      setListLoading(false);
    }
  };

  return (
    <Card data-testid="admin-home-projects-at-risk">
      <CardHeader className="space-y-1 border-b border-slate-100 p-5">
        <h2 className={typography.sectionTitle}>Projects at risk</h2>
        <p className={typography.muted}>
          Projects with at least one open, mitigated or accepted risk.
        </p>
      </CardHeader>
      <CardBody className="space-y-3 p-5">
        {unavailable ? (
          <p
            className={typography.muted}
            data-testid="admin-home-projects-at-risk-unavailable"
          >
            Unavailable
          </p>
        ) : count === 0 ? (
          <p
            className={typography.muted}
            data-testid="admin-home-projects-at-risk-empty"
          >
            No projects at risk
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p
                className="text-3xl font-semibold tabular-nums text-slate-900"
                data-testid="admin-home-projects-at-risk-count"
              >
                {count}
              </p>
              <button
                type="button"
                onClick={() => void toggle()}
                aria-expanded={open}
                className={cn(
                  typography.label,
                  "text-blue-700 hover:text-blue-800",
                )}
                data-testid="admin-home-projects-at-risk-toggle"
              >
                {open ? "Hide projects" : "View projects"}
              </button>
            </div>
            {open ? (
              listLoading ? (
                <p className={typography.muted}>Loading projects…</p>
              ) : listFailed || list === null ? (
                <p
                  className={typography.muted}
                  data-testid="admin-home-projects-at-risk-list-unavailable"
                >
                  Unavailable
                </p>
              ) : (
                <div data-testid="admin-home-projects-at-risk-list">
                  <ul className="space-y-1.5">
                    {list.projects.map((p) => (
                      <li key={p.projectId}>
                        {p.projectName ? (
                          <Link
                            to={`/projects/${p.projectId}`}
                            className="flex items-baseline justify-between gap-3 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 hover:bg-slate-50"
                            data-testid={`admin-home-projects-at-risk-row-${p.projectId}`}
                          >
                            <span className="min-w-0 truncate">
                              {p.projectName}
                              {p.workspaceName ? (
                                <span className="text-slate-500">
                                  {" "}
                                  · {p.workspaceName}
                                </span>
                              ) : null}
                            </span>
                            <span className="shrink-0 tabular-nums text-slate-600">
                              {p.activeRiskCount} active
                            </span>
                          </Link>
                        ) : (
                          <div
                            className="flex items-baseline justify-between gap-3 rounded-md border border-dashed border-slate-200 px-3 py-2 text-sm text-slate-500"
                            data-testid={`admin-home-projects-at-risk-row-${p.projectId}`}
                          >
                            <span>Project no longer available</span>
                            <span className="shrink-0 tabular-nums">
                              {p.activeRiskCount} active
                            </span>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                  {list.total > list.projects.length ? (
                    <p
                      className={cn(typography.muted, "mt-2")}
                      data-testid="admin-home-projects-at-risk-capped"
                    >
                      Showing {list.projects.length} of {list.total} projects,
                      most active risks first.
                    </p>
                  ) : null}
                </div>
              )
            ) : null}
          </>
        )}
      </CardBody>
    </Card>
  );
}
